import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { NotificationContext } from "../utils/common";
import { updateNotification } from "../redux/action/masterAction";
import { DOCUMENT_ACTION } from "../redux/action";
import {
  buildFolderUnreadCounts,
  countDocumentTabUnread,
  getDocumentNotificationsForDocument,
  getDocumentNotificationsForFolder,
  getFolderUnreadFromApiPayload,
  getNotificationDocumentId,
  getNotificationFolderId,
  normalizeDocumentTabUnreadApiData,
  notificationMatchesDocumentScope,
  resolveNotificationFolderId,
} from "../utils/documentTabUnread";
import {
  isDocumentDetailMissing,
  isNotFoundApiResponse,
  isTabUnreadCountsApiUnavailable,
  markDocumentDetailMissing,
  markTabUnreadCountsApiUnavailable,
} from "../utils/documentApiCapability";

/**
 * Tab badges: unread_counts.received (etc.) from getDocumentTabUnreadCounts or getUserDocs.
 * by_folder is folder highlighting only — never summed into tab badge totals.
 */
export function useDocumentTabUnread({
  clientId,
  documentCategory,
  clientBusinessId = null,
  fileStructure,
} = {}) {
  const { notifications, setNotifications } = useContext(NotificationContext) || {
    notifications: [],
    setNotifications: () => {},
  };

  /** Notifications dismissed for client-side fallback badge / folder bold only. */
  const [badgeDismissedIds, setBadgeDismissedIds] = useState(() => new Set());
  /** Document ids the user has visually acknowledged (preview or left folder). */
  const [fileHighlightDismissedIds, setFileHighlightDismissedIds] = useState(
    () => new Set(),
  );
  /** Docs captured when a folder is opened (after badge clears, files stay bold). */
  const [openedFolderNewDocs, setOpenedFolderNewDocs] = useState(
    () => new Map(),
  );
  /** notification_id → folder_id from getDocumentDetails when payload omits folder. */
  const [enrichedFolderByNotifId, setEnrichedFolderByNotifId] = useState({});
  /** Server tab totals + by_folder maps from getDocumentTabUnreadCounts. */
  const [apiUnreadPayload, setApiUnreadPayload] = useState(null);
  const [tabUnreadApiUnavailable, setTabUnreadApiUnavailable] = useState(
    () => isTabUnreadCountsApiUnavailable(),
  );

  const docsTabUnreadFromGetUserDocs = useSelector(
    (state) => state.document?.docsTabUnreadPayload,
  );

  const userRole = localStorage.getItem("user_role") || "";

  const scope = useMemo(
    () => ({
      userRole,
      clientId: clientId != null && clientId !== "" ? String(clientId) : null,
      documentCategory: documentCategory || null,
      clientBusinessId:
        clientBusinessId != null && clientBusinessId !== ""
          ? String(clientBusinessId)
          : null,
    }),
    [userRole, clientId, documentCategory, clientBusinessId],
  );

  const folderMatchContext = useMemo(
    () => ({
      files: fileStructure?.files || [],
      folders: fileStructure?.folders || [],
      enriched: enrichedFolderByNotifId,
    }),
    [fileStructure, enrichedFolderByNotifId],
  );

  const badgeNotifications = useMemo(
    () =>
      (notifications || []).filter(
        (item) => !badgeDismissedIds.has(String(item.notification_id)),
      ),
    [notifications, badgeDismissedIds],
  );

  const fetchTabUnreadCounts = useCallback(async () => {
    if (!scope.clientId || tabUnreadApiUnavailable) {
      if (!scope.clientId) setApiUnreadPayload(null);
      return;
    }

    const response = await DOCUMENT_ACTION.getDocumentTabUnreadCounts({
      client_id: scope.clientId,
      document_category: scope.documentCategory,
      ...(scope.clientBusinessId
        ? { client_business_id: scope.clientBusinessId }
        : {}),
    });

    if (response?.unavailable) {
      markTabUnreadCountsApiUnavailable();
      setTabUnreadApiUnavailable(true);
      setApiUnreadPayload(null);
      return;
    }

    if (response) {
      setApiUnreadPayload(normalizeDocumentTabUnreadApiData(response));
    }
  }, [scope.clientId, scope.documentCategory, tabUnreadApiUnavailable]);

  useEffect(() => {
    fetchTabUnreadCounts();
  }, [fetchTabUnreadCounts, notifications]);

  const fallbackCounts = useMemo(
    () => countDocumentTabUnread(badgeNotifications, scope),
    [badgeNotifications, scope],
  );

  const serverTabUnreadPayload =
    apiUnreadPayload ?? docsTabUnreadFromGetUserDocs ?? null;

  const counts = serverTabUnreadPayload?.counts ?? fallbackCounts;

  // Resolve folder ids for document notifications missing client_folder_id.
  useEffect(() => {
    let cancelled = false;

    const orphans = (notifications || []).filter((item) => {
      if (!notificationMatchesDocumentScope(item, scope)) return false;
      if (getNotificationFolderId(item)) return false;
      if (resolveNotificationFolderId(item, folderMatchContext)) return false;
      const docId = getNotificationDocumentId(item);
      if (docId == null || docId === "") return false;
      return !isDocumentDetailMissing(docId);
    });

    if (!orphans.length) return undefined;

    orphans.forEach((item) => {
      const docId = getNotificationDocumentId(item);
      const notifId = String(item.notification_id ?? "");
      if (!docId || !notifId || enrichedFolderByNotifId[notifId]) return;
      if (isDocumentDetailMissing(docId)) return;

      DOCUMENT_ACTION.getDocumentDetails(docId)
        .then((res) => {
          if (cancelled) return;
          if (isNotFoundApiResponse(res)) {
            markDocumentDetailMissing(docId);
            return;
          }
          const data = res?.data?.data;
          const folderId = data?.client_folder_id ?? data?.folder_id;
          if (folderId == null || folderId === "") return;
          setEnrichedFolderByNotifId((prev) => {
            if (prev[notifId]) return prev;
            return { ...prev, [notifId]: String(folderId) };
          });
        })
        .catch(() => null);
    });

    return () => {
      cancelled = true;
    };
    // enrichedFolderByNotifId intentionally omitted — functional updates only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications, scope, folderMatchContext]);

  const getFolderUnreadCount = useCallback(
    (folderId, tab) => {
      if (folderId == null || folderId === "") return 0;

      if (serverTabUnreadPayload) {
        return getFolderUnreadFromApiPayload(serverTabUnreadPayload, folderId, tab);
      }

      const map = buildFolderUnreadCounts(
        badgeNotifications,
        scope,
        tab,
        folderMatchContext,
      );
      return map[String(folderId)] || 0;
    },
    [serverTabUnreadPayload, badgeNotifications, scope, folderMatchContext],
  );

  const isFileHighlighted = useCallback(
    (documentId, tab) => {
      if (documentId == null || documentId === "") return false;
      const did = String(documentId);
      if (fileHighlightDismissedIds.has(did)) return false;
      if (openedFolderNewDocs.has(did)) return true;
      return (
        getDocumentNotificationsForDocument(
          notifications,
          documentId,
          scope,
          tab,
        ).length > 0
      );
    },
    [notifications, scope, fileHighlightDismissedIds, openedFolderNewDocs],
  );

  const markBadgeNotificationsRead = useCallback(
    async (matches) => {
      if (!matches?.length) return;
      const ids = matches.map((item) => String(item.notification_id));

      const newDocs = new Map();
      matches.forEach((item) => {
        const docId = getNotificationDocumentId(item);
        const folderId = resolveNotificationFolderId(item, folderMatchContext);
        if (docId != null) {
          newDocs.set(String(docId), folderId != null ? String(folderId) : "");
        }
      });

      if (newDocs.size) {
        setOpenedFolderNewDocs((prev) => {
          const next = new Map(prev);
          newDocs.forEach((folderId, docId) => next.set(docId, folderId));
          return next;
        });
      }

      setBadgeDismissedIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.add(id));
        return next;
      });

      await Promise.all(
        matches.map((item) =>
          updateNotification(item.notification_id).catch(() => null),
        ),
      );

      if (typeof setNotifications === "function") {
        setNotifications((prev) => {
          const list = Array.isArray(prev) ? prev : [];
          const ids = new Set(
            matches.map((item) => String(item.notification_id)),
          );
          return list.map((item) =>
            ids.has(String(item.notification_id))
              ? { ...item, view_status: "Viewed" }
              : item,
          );
        });
        setBadgeDismissedIds((prev) => {
          const next = new Set(prev);
          matches.forEach((item) => {
            next.delete(String(item.notification_id));
          });
          return next;
        });
      }

      await fetchTabUnreadCounts();
    },
    [setNotifications, folderMatchContext, fetchTabUnreadCounts],
  );

  const clearFolderUnread = useCallback(
    async (folderId, tab) => {
      const matches = getDocumentNotificationsForFolder(
        notifications,
        folderId,
        scope,
        tab,
        folderMatchContext,
      );
      await markBadgeNotificationsRead(matches);
    },
    [notifications, scope, folderMatchContext, markBadgeNotificationsRead],
  );

  const dismissFileHighlight = useCallback((documentId) => {
    if (documentId == null || documentId === "") return;
    const did = String(documentId);
    setFileHighlightDismissedIds((prev) => {
      if (prev.has(did)) return prev;
      const next = new Set(prev);
      next.add(did);
      return next;
    });
  }, []);

  const dismissFolderFileHighlights = useCallback(
    (folderId, tab) => {
      if (folderId == null || folderId === "") return;
      const fid = String(folderId);
      const docIds = new Set();

      openedFolderNewDocs.forEach((docFolderId, docId) => {
        if (String(docFolderId) === fid) docIds.add(docId);
      });

      getDocumentNotificationsForFolder(
        notifications,
        folderId,
        scope,
        tab,
        folderMatchContext,
      ).forEach((item) => {
        const docId = getNotificationDocumentId(item);
        if (docId != null) docIds.add(String(docId));
      });

      if (!docIds.size) return;

      setFileHighlightDismissedIds((prev) => {
        const next = new Set(prev);
        docIds.forEach((id) => next.add(id));
        return next;
      });
    },
    [notifications, scope, folderMatchContext, openedFolderNewDocs],
  );

  return {
    counts,
    getFolderUnreadCount,
    isFileHighlighted,
    clearFolderUnread,
    dismissFileHighlight,
    dismissFolderFileHighlights,
  };
}
